export function formatLabelWithPrice(label, object, hidePrice = false) {
  if (!object || object.virtual || object.price <= 0 || !window.config.EXTRAS_SHOW_PRICE) {
    return label;
  }

  if (hidePrice) {
    return label;
  }

  const formattedPrice = `${window.config.EXTRAS_SHOW_PRICE_PREFIX} ${object.formattedPrice}`;
  return `${label} (${formattedPrice})`;
}

/** @param {Array<ProductOption>} productOptions */
export function formatProductOptions(productOptions, parentProdId = null, counter = { value: 0 }) {
  let formattedStrings = [];
  let lastOptionProdId = parentProdId; // Track the immediate parent // btw this value will always be ignored / overridden, since level 1 does not useit

  productOptions.forEach((option) => {
    const isSubOpt = option.level > 2;
    const prefix = isSubOpt ? "subOpt" : "opt";

    const currentProdId = option.prodId;

    // For sub-options, assign the last valid parent (immediate upper level)
    const relatedProdId = isSubOpt ? lastOptionProdId : currentProdId;

    const formattedString = `${prefix}_${option.minQty}_${option.maxQty}_${option.id}_${currentProdId}_${counter.value}:${relatedProdId}`;
    formattedStrings.push(formattedString);

    counter.value++;

    // Update lastOptionProdId to track the correct parent for deeper levels
    lastOptionProdId = currentProdId;

    // Pass lastOptionProdId instead of currentProdId to sub-options
    if (option.options && option.options.length > 0) {
      const nestedFormatted = formatProductOptions(
        option.options,      // Sub-options list
        lastOptionProdId,    // Correctly track the immediate parent
        counter
      );
      formattedStrings.push(...nestedFormatted);
    }
  });

  return formattedStrings;
}

/**
 * Cleans and formats product option text by replacing <br> tags and HTML-encoded <br> with commas, removing duplicates and trimming.
 * @param {string} optionText
 * @returns {string}
 */
export function cleanedProdOptionText(optionText) {
  if (!optionText) return '';
  return optionText
    .replace(/(<br\s*\/??\s*>|&lt;br\s*\/??\s*&gt;)/gi, ', ')
    .replace(/\s*,\s*,/g, ',') // Remove duplicate commas
    .replace(/,{2,}/g, ', ') // Replace multiple commas with one
    .replace(/\s{2,}/g, ' ') // Replace multiple spaces with one
    .replace(/^,\s*/, '') // Remove leading comma
    .replace(/,\s*$/, ''); // Remove trailing comma
}

/**
 * Maps the productArray from backend JSON to a simplified product array for frontend use.
 * @param {Array} productArray
 * @returns {Array} Array of mapped product objects
 */
export function mapProductArray(productArray) {
  if (!Array.isArray(productArray)) return [];
  return productArray.map(prod => ({
    id: prod.id,
    name: prod.name,
    description: prod.description,
    formattedPrice: prod.formattedPrice,
    price: prod.price0, // or priceIncTax if needed
    image: prod.image,
    isLocked: !!prod.locked,
    categoryId: prod.categoryId,
    properties: prod.properties || [],
    allergens: prod.allergens || [],
    hasChildren: prod.hasChildren || false
  }));
}

function getMappedProductArray(container) {
  if (Array.isArray(container?.productArray)) {
    return container.productArray;
  }

  if (Array.isArray(container?.productsArray)) {
    return container.productsArray;
  }

  return null;
}

export function getFoodPackagingProductsContainer(source) {
  if (getMappedProductArray(source?.foodPackagingProducts)) {
    return source.foodPackagingProducts;
  }

  if (getMappedProductArray(source?.foodPackaingProducts)) {
    return source.foodPackaingProducts;
  }

  if (getMappedProductArray(source?.products?.foodPackagingProducts)) {
    return source.products.foodPackagingProducts;
  }

  if (getMappedProductArray(source?.products?.foodPackaingProducts)) {
    return source.products.foodPackaingProducts;
  }

  if (getMappedProductArray(source?.basketTile?.foodPackagingProducts)) {
    return source.basketTile.foodPackagingProducts;
  }

  if (getMappedProductArray(source?.basketTile?.foodPackaingProducts)) {
    return source.basketTile.foodPackaingProducts;
  }

  if (Array.isArray(source?.categories)) {
    const categoryWithFoodPackagingProducts = source.categories.find((category) => getMappedProductArray(category?.foodPackagingProducts));
    if (getMappedProductArray(categoryWithFoodPackagingProducts?.foodPackagingProducts)) {
      return categoryWithFoodPackagingProducts.foodPackagingProducts;
    }

    const categoryWithMisspelledFoodPackagingProducts = source.categories.find((category) => getMappedProductArray(category?.foodPackaingProducts));
    if (getMappedProductArray(categoryWithMisspelledFoodPackagingProducts?.foodPackaingProducts)) {
      return categoryWithMisspelledFoodPackagingProducts.foodPackaingProducts;
    }
  }

  if (getMappedProductArray(source)) {
    return source;
  }

  return null;
}

export function mapFoodPackagingProducts(source) {
  const packagingProducts = getFoodPackagingProductsContainer(source);
  return mapProductArray(getMappedProductArray(packagingProducts));
}

